import { goToCustomizeExistingTemplate } from '@/services/checklistTemplate/checklistBuiler';
import { h, defineComponent, onUnmounted, ref } from 'vue';
import { templates } from '@/ui/auth/templates';
import { Template } from '@/entities/template/model';
import { ModalSidePanel } from '@/ui/components/ModalSidePanel';
import { pageSubtitle, pageTitle, tag } from '@/ui/components';
import { Button } from '@/ui/components/Button';
import { tooltip } from '@/ui/components/Tooltip';
import { useTask } from '@/ui/concerns';
import { createChecklistFromTemplate, get as getTemplate } from '@/services/api/resources/template/actions';
import TabletFrame from '@/_legacy/components/TabletFrame.vue';
import ConductInspectionPreview from '@/_legacy/components/ConductInspection/ConductInspectionPreview.vue';
import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
import { CHECKLIST_STATUS } from '@/_legacy/constants';
import { pushRoute, toasted } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';
import { Loading } from '@/ui/components/Loading';
import { featureExplanationIcon } from '@/ui/components/icon/icons';
import { generateHeaderPage } from '@/_legacy/services/checklist';
import { Userflow, UserflowTrackEvent, UserflowTrackEventData } from '@/services/userflow/userflow';
import { v4 as uuidv4 } from 'uuid';

interface TemplateListProps {
  isFetching: boolean;
}

export const TemplateList = defineComponent<TemplateListProps>({
  name: 'TemplateList',
  props: ['isFetching'],
  setup(props) {
    const loaders = ref({
      creatingChecklist: false,
    });
    const isModalActive = ref(false);
    const activeTemplate = ref<Template | null>(null);
    const getTemplatesTask = useTask(
      (id: number | undefined) => getTemplate(id as number),
      templateResponse => {
        activeTemplate.value = templateResponse.data.data;
        isModalActive.value = true;
      },
    );
    const setActiveTemplate = (template: Template) => {
      getTemplatesTask.run(template.id);
    };
    const trackTemplateClick = (template: Template) => {
      trackClick(CLICK_EVENTS.PREVIEW_TEMPLATE, { label: template.title });
    };
    const highlightCurrentTemplate = (template: Template): string => {
      if (activeTemplate.value) {
        return template.id === activeTemplate.value.id ? 'checklist-templates__list-item--active' : '';
      }

      return '';
    };
    const encodeTemplateIntoChecklist = (template: Record<string, unknown> = {}) => ({
      checklist_title: template.title,
      checklist_description: template.description,
      entity_items: template.entityItems || [],
      categories: template.categories || [],
      report_template: template.report_template || null,
      form: template.form || {},
      hasUserAutomatic: template.hasUserAutomatic || true,
      hasDatetimeAutomatic: template.hasDatetimeAutomatic || true,
      status: template.status || CHECKLIST_STATUS.ACTIVE,
      schedules: template.schedules || [],
      restrictions: template.restrictions || null,
    });
    const createTemplateTask = useTask(
      (body: undefined | FormData) => createChecklistFromTemplate(body!),
      () => {
        Userflow.trackEvent(UserflowTrackEvent.TEMPLATE_CREATED, { [UserflowTrackEventData.TEMPLATE_NAME]: activeTemplate.value?.title });
        toasted.success(t('checklist-templates.checklist-saved'));
        pushRoute({ name: 'forms' });
        loaders.value.creatingChecklist = false;
      },
      uuidv4(),
      error => {
        toasted.error(error.message);
        loaders.value.creatingChecklist = false;
      },
    );
    const onUseThisTemplateClick = async () => {
      if (!activeTemplate.value) {
        return;
      }

      trackClick({
        ...CLICK_EVENTS.CREATE_READY_MADE_TEMPLATE,
        'template-name': activeTemplate.value.title,
        'is-suggested': activeTemplate.value.isFeatured,
      });

      // Since we are dealing with legacy endpoint we need to encode into a form data
      // as soon as we refactor checklist feature we should refactor this as well
      const formData = new FormData();
      const encodedTemplate = encodeTemplateIntoChecklist(activeTemplate.value);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formItems = (encodedTemplate.form as any).items;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (!formItems.some(((item: any) => item.params?.header === true))) {
        formItems.unshift(generateHeaderPage());
      }
      const payload = JSON.stringify(encodedTemplate);
      formData.append('checklist', payload);
      formData.append('fromTemplate', String(activeTemplate.value.id));

      loaders.value.creatingChecklist = true;
      createTemplateTask.run(formData);
    };
    const onCustomizeTemplateClick = () => {
      if (!activeTemplate.value) {
        return;
      }

      goToCustomizeExistingTemplate(activeTemplate.value);
    };

    const clearStates = () => {
      templates.value = [];
    };

    onUnmounted(clearStates);

    return function render() {
      return h('div', { class: 'checklist-templates__list' }, [
        h(ModalSidePanel, {
          props: {
            right: 0,
            top: 0,
            width: '497px',
            active: isModalActive.value,
          },
          on: {
            close: () => {
              activeTemplate.value = null;
              isModalActive.value = false;
            },
            'click:outside': (event: MouseEvent) => {
              if ((event.target as HTMLElement).dataset.template) {
                return;
              }

              // if user clicks on another template we don't want to close the modal
              // therefore we will be getting a new template
              if (getTemplatesTask.state.value.pending) {
                return;
              }

              activeTemplate.value = null;
              isModalActive.value = false;
            },
          },
        }, [
          activeTemplate.value
            ? [
              h('template', { slot: 'title' }, [
                pageSubtitle(activeTemplate.value.title, 'checklist-templates__preview-title'),
              ]),
              h('div', { class: 'has-text-centered' }, activeTemplate.value.description),
              h('div', { class: 'checklist-templates__choices-row' }, [
                h('div', { class: 'checklist-templates__choice-row' }, [
                  h(Button, {
                    props: { loading: loaders.value.creatingChecklist },
                    on: { click: onUseThisTemplateClick },
                  }, t('checklist-templates.use-this-template')),
                  tooltip({
                    label: t('checklist-templates.use-template-tooltip'),
                    multilined: true,
                    position: 'is-bottom',
                    classes: 'ml-2',
                  }, [featureExplanationIcon()], 'label'),
                ]),
                h('div', { class: 'checklist-templates__choice-row' }, [
                  h(Button, {
                    props: {
                      outlined: true,
                      btnClass: 'ml-4',
                    },
                    on: {
                      click: onCustomizeTemplateClick,
                    },
                  }, t('checklist-templates.customize-template')),
                  tooltip({
                    label: t('checklist-templates.customize-tooltip'),
                    multilined: true,
                    position: 'is-left',
                    classes: 'ml-2',
                  }, [featureExplanationIcon()], 'label'),
                ]),
              ]),
              h('div', { class: 'checklist-templates__template-preview-title' }, 'Template preview'),
              h(TabletFrame, { class: 'template-modal__preview' }, [
                //legacy, not compatible with Vue typings
                //@ts-ignore
                h(ConductInspectionPreview, {
                  props: {
                    'preview-mode': 'tablet',
                    inspection: activeTemplate.value,
                    'on-submit': () => {},
                  },
                }),
              ]),
            ] : null,
        ]),
        h(Loading, { props: { active: props.isFetching } }),
        templates.value.length < 1 && !props.isFetching
          ? pageTitle(t('checklist-templates.zero-templates'), 'has-text-centered py-2')
          : null,
        templates.value.map((template, index) => h('div',
          {
            class: `checklist-templates__list-item ${highlightCurrentTemplate(template)}`,
            attrs: {
              'data-template': true,
            },
            on: {
              click: () => {
                setActiveTemplate(template);
                trackTemplateClick(template);
              },
            },
          },
          [
            h('div', {
              class: {
                flex: true,
                'checklist-templates__list-item-row': true,
                'checklist-templates__list-item-row--has-bottom-border': index !== templates.value.length - 1,
              },
            }, [
              h('div', { class: 'checklist-templates__list-item-title' }, template.title),
              template.isFeatured && tag('success', t('common.featured')),
            ]),
          ])),
      ]);
    };
  },
});
